import { useEffect, useState } from 'react';
import { Bell, Check, Share } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { currentPushState, enablePush, disablePush, sendTestPush, type PushState } from '../lib/push';

// Owner/lessee opt-in for iPhone (Web Push) notifications. Hides itself where push
// can't work (demo, unsupported browser). On iOS it gates on "Add to Home Screen"
// first, because Web Push only works in the installed PWA.
export function NotificationsToggle() {
  const { fbUser, profile, isAdmin, demoMode } = useAuth();
  const [state, setState] = useState<PushState | 'loading'>('loading');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const uid = profile?.uid || fbUser?.uid;
  const role: 'owner' | 'lessee' = isAdmin ? 'owner' : 'lessee';

  useEffect(() => {
    if (demoMode) {
      setState('unsupported');
      return;
    }
    currentPushState()
      .then(setState)
      .catch(() => setState('unsupported'));
  }, [demoMode]);

  if (demoMode || state === 'loading' || state === 'unsupported') return null;

  async function enable() {
    if (!uid) return;
    setBusy(true);
    setMsg(null);
    try {
      await enablePush(uid, role);
      setState('subscribed');
      await sendTestPush(uid);
      setMsg('Notifications are on — a test should pop up in a moment.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Could not enable notifications.');
    } finally {
      setBusy(false);
    }
  }
  async function turnOff() {
    setBusy(true);
    try {
      await disablePush();
      setState('ready');
      setMsg(null);
    } finally {
      setBusy(false);
    }
  }
  async function test() {
    if (!uid) return;
    await sendTestPush(uid);
    setMsg('Test sent — check your notifications.');
  }

  return (
    <div className="card p-5">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-brand-50">
          <Bell size={18} className="text-navy-700" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-base font-semibold text-navy-900">Phone notifications</h3>

          {state === 'needs-install' && (
            <div className="mt-1 text-sm text-ink-muted">
              <p>
                To get alerts on your iPhone (takeoffs, landings, squawks) without checking email, add
                this app to your Home Screen first:
              </p>
              <ol className="mt-2 list-decimal space-y-1 pl-5">
                <li>
                  Tap the <Share size={13} className="inline align-text-bottom" /> Share button in Safari
                </li>
                <li>Choose “Add to Home Screen”</li>
                <li>Open AJM Aviation from your Home Screen, then come back here to turn them on</li>
              </ol>
            </div>
          )}

          {state === 'denied' && (
            <p className="mt-1 text-sm text-ink-muted">
              Notifications are blocked for this app. Turn them back on in your device Settings →
              Notifications → AJM Aviation, then reload.
            </p>
          )}

          {state === 'ready' && (
            <>
              <p className="mt-1 text-sm text-ink-muted">
                Get takeoffs, landings, new squawks and pilots-to-review pushed straight to this device —
                no email needed.
              </p>
              <button onClick={enable} disabled={busy} className="btn-primary mt-3 text-sm">
                {busy ? 'Turning on…' : 'Turn on notifications'}
              </button>
            </>
          )}

          {state === 'subscribed' && (
            <>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-emerald-700">
                <Check size={15} /> Notifications are on for this device.
              </p>
              <div className="mt-3 flex gap-2">
                <button onClick={test} disabled={busy} className="btn-secondary text-sm">
                  Send a test
                </button>
                <button onClick={turnOff} disabled={busy} className="btn-ghost text-sm">
                  Turn off
                </button>
              </div>
            </>
          )}

          {msg && <p className="mt-2 text-xs text-ink-muted">{msg}</p>}
        </div>
      </div>
    </div>
  );
}
